import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeft, Receipt, Loader2, Camera, ImageOff, ImagePlus, Eye } from "lucide-react";
import { toast } from "sonner";
import ReceiptPreview, { ReceiptThumbnail } from "@/components/ReceiptPreview";

const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RECEIPT_FILTERS = [
  { key: "all",     label: "All",             match: () => true },
  { key: "with",    label: "With receipt",    match: (e) => !!e.receipt_file_id },
  { key: "missing", label: "Missing receipt", match: (e) => !e.receipt_file_id },
];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newForm(""));
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receiptFilter, setReceiptFilter] = useState("all");
  const [previewTarget, setPreviewTarget] = useState(null);
  const [q, setQ] = useState("");

  function newForm(catId) { return { date: new Date().toISOString().slice(0, 10), vendor: "", amount: "", category_id: catId || "", payment_method: "", description: "", tax_deductible: true, receipt_file_id: "" }; }

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([api.get("/expenses"), api.get("/expenses/categories")]);
      setItems(e.data);
      setCats(c.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(newForm(cats[0]?.id));
    setReceiptFile(null);
    setReceiptPreviewUrl(null);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ date: (it.date || "").slice(0, 10), vendor: it.vendor || "", amount: String(it.amount || ""), category_id: it.category_id || "", payment_method: it.payment_method || "", description: it.description || "", tax_deductible: !!it.tax_deductible, receipt_file_id: it.receipt_file_id || "" });
    setReceiptFile(null);
    setReceiptPreviewUrl(null);
    setOpen(true);
  };

  const onFilePicked = (file) => {
    if (!file) { setReceiptFile(null); setReceiptPreviewUrl(null); return; }
    setReceiptFile(file);
    // Local preview
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setReceiptPreviewUrl(url);
    } else {
      setReceiptPreviewUrl(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let receiptId = form.receipt_file_id;
      if (receiptFile) {
        const { data: b } = await api.get("/business");
        const fd = new FormData();
        fd.append("file", receiptFile);
        fd.append("resource_type", "business");
        fd.append("resource_id", b.id);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receiptId = data.id;
      }
      const payload = { ...form, amount: parseFloat(form.amount || 0), receipt_file_id: receiptId || null };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const counts = useMemo(() => RECEIPT_FILTERS.reduce((acc, f) => { acc[f.key] = items.filter(f.match).length; return acc; }, {}), [items]);
  const activeFilter = RECEIPT_FILTERS.find((f) => f.key === receiptFilter) || RECEIPT_FILTERS[0];
  const filtered = items.filter((it) => activeFilter.match(it) && (
    !q || (it.vendor || "").toLowerCase().includes(q.toLowerCase()) || (it.description || "").toLowerCase().includes(q.toLowerCase())
  ));
  const total = filtered.reduce((s, i) => s + Number(i.amount || 0), 0);
  const missingCount = counts.missing || 0;

  return (
    <div className="p-4 sm:p-8 max-w-6xl">
      <div className="mb-4"><Link to="/app/financials" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Financials</Link></div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Financials</div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3"><Receipt className="w-7 h-7 text-[#FF5A00]"/> Expenses</h1>
          <div className="text-sm text-zinc-500 mt-1">{filtered.length} showing · Total {money(total)}</div>
        </div>
        <Button onClick={openNew} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="expenses-add-btn"><Plus className="w-4 h-4 mr-1"/> Add expense</Button>
      </div>

      {missingCount > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3" data-testid="missing-receipt-banner">
          <ImageOff className="w-5 h-5 text-amber-600"/>
          <div className="flex-1 text-sm">
            <div className="font-medium text-amber-900">{missingCount} expense{missingCount === 1 ? "" : "s"} missing a receipt</div>
            <div className="text-xs text-amber-700">Attach one for tax season by clicking the row.</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setReceiptFilter("missing")} className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="jump-to-missing-btn">Show missing</Button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-3 border-b border-zinc-100 flex flex-wrap items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or notes" className="flex-1 min-w-[200px] h-9"/>
          <div className="flex gap-1 flex-wrap">
            {RECEIPT_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setReceiptFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${receiptFilter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                data-testid={`expense-receipt-filter-${f.key}`}
              >
                {f.label}
                <span className={`px-1 rounded font-mono-accent text-[9px] ${receiptFilter === f.key ? "bg-white/20" : "bg-zinc-100"}`}>{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-1">Receipt</div>
          <div className="col-span-1">Date</div>
          <div className="col-span-3">Vendor</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2">Method</div>
          <div className="col-span-1 text-right">•</div>
        </div>

        {loading ? <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
        : filtered.length === 0 ? <div className="p-12 text-center text-zinc-400">No expenses match this filter.</div>
        : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((it) => (
              <div key={it.id} className="md:grid md:grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-zinc-50/50" data-testid={`expense-row-${it.id}`}>
                <div className="md:col-span-1 flex items-center gap-2 md:block mb-2 md:mb-0">
                  <ReceiptThumbnail expense={it} onOpen={() => it.receipt_file_id && setPreviewTarget(it)}/>
                  <div className="md:hidden text-xs text-zinc-500 flex-1">{(it.date || "").slice(0, 10)}</div>
                </div>
                <div className="md:col-span-1 hidden md:block text-sm font-mono-accent text-zinc-600">{(it.date || "").slice(0, 10)}</div>
                <div className="md:col-span-3 cursor-pointer" onClick={() => openEdit(it)}>
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {it.vendor || <span className="text-zinc-400 italic">Untitled</span>}
                    {!it.receipt_file_id && <span className="inline-flex items-center gap-0.5 text-[9px] font-mono-accent uppercase tracking-widest px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"><ImageOff className="w-2.5 h-2.5"/> No receipt</span>}
                  </div>
                  {it.description && <div className="text-xs text-zinc-500 truncate max-w-md">{it.description}</div>}
                </div>
                <div className="md:col-span-2 mt-1 md:mt-0">
                  <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-mono-accent uppercase text-[10px]">{it.category_name}</span>
                </div>
                <div className="md:col-span-2 md:text-right text-sm font-mono-accent font-semibold text-rose-600 mt-1 md:mt-0">{money(it.amount)}</div>
                <div className="md:col-span-2 text-xs text-zinc-500 truncate mt-1 md:mt-0">{it.payment_method || "—"}</div>
                <div className="md:col-span-1 flex md:justify-end justify-start gap-1 mt-2 md:mt-0">
                  {it.receipt_file_id && (
                    <button onClick={() => setPreviewTarget(it)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600" data-testid={`expense-view-receipt-${it.id}`} title="View receipt">
                      <Eye className="w-4 h-4"/>
                    </button>
                  )}
                  <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600 md:hidden" data-testid={`expense-edit-${it.id}`} title="Edit">
                    <ImagePlus className="w-4 h-4"/>
                  </button>
                  <button onClick={() => del(it.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`expense-delete-${it.id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit expense" : "Add expense"}</DialogTitle>
            <DialogDescription>Attach a receipt if you have one — snap a photo on mobile or upload an image/PDF.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input"/></div>
            <div><Label>Amount</Label><Input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input"/></div>
            <div className="col-span-2"><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor-input"/></div>
            <div><Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="expense-cat-select"><SelectValue placeholder="Select category"/></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Payment method</Label><Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="Credit card, cash…" data-testid="expense-method-input"/></div>
            <div className="col-span-2"><Label>Description / notes</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc-input"/></div>
            <div className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.tax_deductible} onChange={(e) => setForm({ ...form, tax_deductible: e.target.checked })} data-testid="expense-deductible-input"/>
              <span>Tax deductible</span>
            </div>
            <div className="col-span-2 border border-dashed border-zinc-300 rounded-lg p-3">
              <Label>Receipt (image or PDF)</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-[#FF5A00]" data-testid="expense-receipt-camera-btn">
                  <Camera className="w-4 h-4 text-[#FF5A00]"/> Take photo
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => onFilePicked(e.target.files?.[0])} className="hidden" data-testid="expense-receipt-camera-input"/>
                </label>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-[#FF5A00]" data-testid="expense-receipt-upload-btn">
                  <ImagePlus className="w-4 h-4 text-zinc-600"/> Upload file
                  <input type="file" accept="image/*,application/pdf" onChange={(e) => onFilePicked(e.target.files?.[0])} className="hidden" data-testid="expense-receipt-input"/>
                </label>
              </div>
              {receiptPreviewUrl && (
                <div className="mt-3 relative inline-block">
                  <img src={receiptPreviewUrl} alt="preview" className="max-h-40 rounded border border-zinc-200" data-testid="expense-receipt-local-preview"/>
                  <button type="button" onClick={() => onFilePicked(null)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow" title="Remove"><ImageOff className="w-3 h-3"/></button>
                </div>
              )}
              {!receiptFile && form.receipt_file_id && (
                <div className="text-xs text-zinc-500 mt-2 inline-flex items-center gap-1"><Eye className="w-3 h-3"/> Existing receipt attached. Uploading a new file will replace it.</div>
              )}
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="expense-save-btn">{busy ? "Saving…" : editing ? "Update" : "Add expense"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ReceiptPreview
        expense={previewTarget}
        open={!!previewTarget}
        onOpenChange={(v) => { if (!v) setPreviewTarget(null); }}
        onReplaced={(updated) => { setPreviewTarget(null); load(); }}
      />
    </div>
  );
}
